import { cloneDeep, isEqual } from 'lodash'
import { ref, type Ref, watch } from 'vue'
import { useLoadListItem } from '@/entities/Inventory/ListItem/model/api'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { normalizeRuntimeFieldValues, type RuntimeFieldValueMap } from './runtimeFieldBindings'

export type ObjectPassportFieldValue = {
  id: string | number
  value?: unknown
}

type ObjectPassportTypeConfig = {
  id?: number
  fields: ObjectInventoryField[]
  files: any[]
  properties: {
    is_comment: boolean
    is_journal_replace: boolean
    is_must_accept?: boolean
  }
  groups: any[]
}

export type ObjectPassportElement = {
  id?: number
  invent_objects_type_id?: number
  name: string
  fields: ObjectPassportFieldValue[] | RuntimeFieldValueMap
  invent_object_type: ObjectPassportTypeConfig
  object_type: {
    id?: number
    object_name: string
  }
  parent_object: {
    id: number
    name: string
    object_type?: {
      object_name: string
    }
  }
  status_dates: any[]
  relation_links: any[]
  is_projecting?: boolean
  connection_object_list_id?: number | null
  files?: any
  comment?: string
  accept_status?: string
  stats?: any
  [key: string]: unknown
}

export type ObjectPassportSnapshot = {
  name: string
  is_projecting: boolean
  connection_object_list_id: number | null
  fields: RuntimeFieldValueMap
  files: any
  comment: string
}

export function createEmptyObjectPassportElement(): ObjectPassportElement {
  return {
    name: '',
    fields: [],
    invent_object_type: {
      fields: [],
      files: [],
      properties: {
        is_comment: false,
        is_journal_replace: false,
      },
      groups: [],
    },
    object_type: {
      id: undefined,
      object_name: '',
    },
    parent_object: {
      id: 0,
      name: '',
    },
    status_dates: [],
    relation_links: [],
  }
}

export function mergeLoadedObjectPassportElement(
  current: ObjectPassportElement,
  incoming: Partial<ObjectPassportElement>,
): ObjectPassportElement {
  return {
    ...current,
    ...incoming,
  }
}

export function createObjectPassportSnapshot(
  element: ObjectPassportElement,
  fieldValues: RuntimeFieldValueMap,
  fieldMetadata: ObjectInventoryField[] | null | undefined = element.invent_object_type?.fields,
): ObjectPassportSnapshot {
  return {
    name: element.name,
    is_projecting: Boolean(element.is_projecting),
    connection_object_list_id:
      (element.connection_object_list_id as number | null | undefined) ?? null,
    fields: normalizeRuntimeFieldValues(fieldValues, fieldMetadata),
    files: element.files,
    comment: String(element.comment ?? ''),
  }
}

export function cloneObjectPassportSnapshot(
  element: ObjectPassportElement,
  fieldValues: RuntimeFieldValueMap,
  fieldMetadata: ObjectInventoryField[] | null | undefined = element.invent_object_type?.fields,
): ObjectPassportSnapshot {
  return cloneDeep(createObjectPassportSnapshot(element, fieldValues, fieldMetadata))
}

export function hasObjectPassportChanges(
  initialSnapshot: ObjectPassportSnapshot | null,
  element: ObjectPassportElement | null,
  fieldValues: RuntimeFieldValueMap,
  fieldMetadata: ObjectInventoryField[] | null | undefined = element?.invent_object_type?.fields,
): boolean {
  if (!initialSnapshot || !element) return false
  return !isEqual(createObjectPassportSnapshot(element, fieldValues, fieldMetadata), initialSnapshot)
}

export const useModel = () => {
  const data = ref<any>(createEmptyObjectPassportElement())

  /** Фильтр по данным */
  const filter = ref<{ status_date: null | string }>({
    status_date: null,
  })

  /**
   * Функция загрузки данных
   * @param id
   */
  const loadData = (id: Ref<number>) => {
    const result = useLoadListItem(id, filter)

    watch(result.data, () => {
      if (result.data.value?.data[0]) {
        data.value = mergeLoadedObjectPassportElement(data.value, result.data.value.data[0])
      }
    })

    return result
  }

  return {
    data,
    filter,

    loadData,
  }
}
